import React, { useCallback } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Box, Button, Heading, Paragraph, Text } from '@rover/kibble/core';
import type { FrontendConfigurationSettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import Separator from '@rover/shared/js/components/Separator';
import useAvailablePets from '@rover/shared/js/hooks/useAvailablePets';
import PetSize from '@rover/shared/js/search/components/PetSize';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';

import { shouldShowControls } from '../../../utilities';
import { usePetInfoCalloutsExperiment } from '../../../utilities/usePetInfoCalloutsExperiment';
import { WizardPageType } from '../../../WizardPageType';
import { CatPreferences, DogPreferences } from '../../Filters';
import InlinePetCounter from '../../PetCounter/InlinePetCounter';
import {
  SearchDesktopDeselectPet,
  SearchDesktopSelectPet,
  SearchDesktopUpdatePetType,
  SearchDesktopUpdateSize,
} from '../analyticsEvents';

// Helper to derive petType array from counts
const getPetTypeFromCounts = (dog: number, cat: number, puppy: number): PetType[] => {
  const types: PetType[] = [];
  // Include DOG type if there are dogs OR puppies (puppies are dogs < 1 year old)
  if (dog > 0 || puppy > 0) types.push(PetType.DOG);
  if (cat > 0) types.push(PetType.CAT);
  return types;
};

type PetsDropdownContentProps = {
  allPets: Pet[];
  searchFilters: SearchFilters;
  dogCount: number;
  catCount: number;
  puppyCount: number;
  petSizeBuckets: FrontendConfigurationSettingsDogSizeBuckets;
  weightUnit: string;
  onChange: (filters: Partial<SearchFilters>) => void;
  onSave: () => void;
  serviceTypeFilterOptions: SearchFiltersOptions;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
};

const PetsDropdownContent = ({
  allPets,
  searchFilters,
  dogCount,
  catCount,
  puppyCount,
  petSizeBuckets,
  weightUnit,
  onChange,
  onSave,
  serviceTypeFilterOptions,
  getServiceTypeBySlug,
}: PetsDropdownContentProps): JSX.Element => {
  const { t } = useLingui();
  const availablePets = useAvailablePets(searchFilters.serviceType);

  const petsTitleText = (): string => t`Which pets need care?`;
  const petCounterTitleText = (): string => t`How many pets need care?`;
  const petSubtitleText = (): string =>
    t`Add at least one pet. This helps you see the most accurate pricing.`;
  const petCounterSubtitleText = (): string =>
    t`Add at least one pet. This helps you find a sitter and see the most accurate pricing.`;

  const userHasNoPets = allPets.length === 0;
  const titleText = userHasNoPets ? petCounterTitleText() : petsTitleText();
  const subtitleText = userHasNoPets ? petCounterSubtitleText() : petSubtitleText();
  const isServiceCatOnly =
    availablePets.length > 0 && availablePets.every((pet) => pet === PetType.CAT);
  const isCatOnly = isServiceCatOnly || getIsCatOnlyPetType(searchFilters.petType || []);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const { isActive: inPetInfoCalloutsExperiment } = usePetInfoCalloutsExperiment();

  const { showPetSize, showCatPreferences, showDogPreferences } = shouldShowControls({
    hasPets: !userHasNoPets,
    isCatOnly,
    pageType: WizardPageType.DEFAULT,
    serviceOptions: serviceTypeFilterOptions,
    serviceType: serviceType || undefined,
    serviceTypeSlug: searchFilters.serviceType,
    inPetInfoCalloutsExperiment,
  });

  const handlePetCountChange = useCallback(
    (field: 'dogCount' | 'catCount' | 'puppyCount') => (value: React.SetStateAction<number>) => {
      const currentCounts = { dogCount, catCount, puppyCount };
      const newValue = typeof value === 'function' ? value(currentCounts[field]) : value;

      const newCounts = { ...currentCounts, [field]: newValue };

      const petType = getPetTypeFromCounts(
        newCounts.dogCount,
        newCounts.catCount,
        newCounts.puppyCount
      );

      // Update both count and petType in a single onChange call
      onChange({ [field]: newValue, petType });
      emitAnalyticsEvent(
        new SearchDesktopUpdatePetType({
          dog: newCounts.dogCount > 0,
          cat: newCounts.catCount > 0,
          puppy: newCounts.puppyCount > 0,
        })
      );
    },
    [dogCount, catCount, puppyCount, onChange]
  );

  const handlePetSelectionChange = (filters: Partial<SearchFilters>): void => {
    if (filters.pet && Array.isArray(filters.pet)) {
      const currentPetIds = searchFilters.pet || [];
      const newPetIds = filters.pet;

      const addedPets = newPetIds.filter((petId) => !currentPetIds.includes(petId));
      addedPets.forEach(() => {
        emitAnalyticsEvent(new SearchDesktopSelectPet());
      });

      const removedPets = currentPetIds.filter((petId) => !newPetIds.includes(petId));
      removedPets.forEach(() => {
        emitAnalyticsEvent(new SearchDesktopDeselectPet());
      });
    }

    onChange(filters);
  };

  const handleDogSizeChange = (sizeKey: string | number): void => {
    emitAnalyticsEvent(new SearchDesktopUpdateSize());
    onChange({ [sizeKey]: !searchFilters[sizeKey as keyof SearchFilters] });
  };

  return (
    <Box
      width="100%"
      backgroundColor="white"
      borderRadius="primary"
      sx={{ display: 'flex', flexDirection: 'column', maxHeight: '500px', overflow: 'hidden' }}
    >
      <Box p="6x" sx={{ textAlign: 'left', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
        <Heading size="300" textAlign="left" mb="4x">
          {t`Your pets`}
        </Heading>
        <InlinePetCounter
          pets={allPets}
          dogs={dogCount || 0}
          cats={catCount || 0}
          puppies={puppyCount || 0}
          availablePets={availablePets}
          triggeredFrom="Search"
          setDogs={handlePetCountChange('dogCount')}
          setPuppies={handlePetCountChange('puppyCount')}
          setCats={handlePetCountChange('catCount')}
          titleText={titleText}
          subtitleText={subtitleText}
          searchFilters={searchFilters}
          onPetSelectionChange={handlePetSelectionChange}
        />
        {showPetSize && petSizeBuckets && (
          <Box pb="6x" data-testid="pet-size-selector">
            <Separator mt="0x" mb="4x" />
            <PetSize
              displayAsIconButtons
              label={t`What size are your dogs (${weightUnit})?`}
              labelColor="primary"
              subLabel={t`Select all that apply.`}
              searchFilters={searchFilters}
              weightUnit={weightUnit}
              onChange={handleDogSizeChange}
              petSizeBuckets={petSizeBuckets}
              showCheckmark={false}
            />
          </Box>
        )}
        {(showCatPreferences || showDogPreferences) && <Separator mt="0x" mb="4x" />}
        {(showCatPreferences || showDogPreferences) && (
          <Box aria-label={t`Tell us about your pets`} mb="6x">
            <Text size="200" fontWeight="semibold">
              {t`Tell us about your pets`}
            </Text>
            <Paragraph
              size="100"
              textColor="tertiary"
            >{t`We'll show sitters with relevant experience.`}</Paragraph>
            {showCatPreferences && (
              <Box mb={showDogPreferences ? '4x' : undefined}>
                <CatPreferences
                  displayAsToggle
                  onChange={(noCats) => onChange({ noCats })}
                  value={searchFilters.noCats}
                />
              </Box>
            )}
            {showDogPreferences && (
              <DogPreferences
                displayAsToggle
                onChange={(personDoesNotHaveDogs) => onChange({ personDoesNotHaveDogs })}
                value={searchFilters.personDoesNotHaveDogs}
              />
            )}
          </Box>
        )}
      </Box>
      <Box px="6x" pb="6x" pt="4x" backgroundColor="white" sx={{ flexShrink: 0 }}>
        <Button variant="primary" fullWidth size="small" onPress={onSave}>
          {t`Save`}
        </Button>
      </Box>
    </Box>
  );
};

export default PetsDropdownContent;
